import { ReactElement, useEffect, useMemo, useState } from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	Dialog,
	FormattedText,
	renderReactDialog,
	Form,
	FormItem,
	TextInput,
	ValidationError,
	InputWithMoreInfo,
	FindSettingDialog,
	Toolbar
} from "client/Components";
import { useFormHandler, IFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	isNotNullNorUndefined,
	isNotEmpty,
	doesNotExceedLength,
	isUrl
} from "client/Validation/rules";
import {
	LiquitConnectionSaveModel,
	create,
	update,
	get,
	LiquitConnectionListItemModel
} from "client/Api/LiquitConfiguration/liquitConfiguration";
import { AlertManager, useAlerts, useUnmountedCancellation} from "client/Hooks";
import { generateTrackingId } from "client/Misc/utils";

export type LiquitConnectionDialogProps = {
	connectionId?: number;
	onAfterSave(item: LiquitConnectionListItemModel): void;
	onClose(): void;
};

const validationConfiguration: IValidationConfiguration<LiquitConnectionSaveModel> = createValidationConfiguration<LiquitConnectionSaveModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationConfiguration.forScalar("portalUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true,
			allowQueryString: false
		})
	);

validationConfiguration.forScalar("identitySource")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forScalar("applicationId")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export const LiquitConnectionDialog = (props: LiquitConnectionDialogProps): ReactElement => {
	const {
		connectionId,
		onAfterSave,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [model, setModel] = useState<LiquitConnectionSaveModel>({
		name: "",
		portalUrl: "",
		identitySource: "",
		applicationId: ""
	});
	const [activeMoreInfoSetting, setActiveMoreInfoSetting] = useState<FindableSetting | undefined>(undefined);
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<LiquitConnectionSaveModel> = useFormHandler<LiquitConnectionSaveModel>(
		model,
		setModel,
		validationConfiguration
	);

	const activeFindableSetting = useMemo(
		(): FindableSettingEntry | undefined => {
			if (!activeMoreInfoSetting) {
				return undefined;
			}

			return _findSettingConfiguration[activeMoreInfoSetting];
		},
		[activeMoreInfoSetting]
	);

	useEffect(
		() => {
			if (connectionId === undefined) {
				return undefined;
			}

			const loadConnection = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const {
						name,
						portalUrl,
						identitySource,
						applicationId
					} = await get(
							connectionId,
							signal
						);

					setModel({
						name,
						portalUrl,
						identitySource,
						applicationId
					});

					setIsBusy(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			loadConnection(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, connectionId, onClose]
	);

	const isNewConnection: boolean = connectionId === undefined;

	let titleKey: LocalizationKey;
	let saveKey: LocalizationKey;
	if (isNewConnection) {
		titleKey = "_NewNameConnection";
		saveKey = "_Add";
	} else {
		titleKey = "_EditNameConnection";
		saveKey = "_Save";
	}

	const submitHandler = async (): Promise<void> => {
		if (isBusy) {
			return;
		}

		setIsBusy(true);
		const validationResult: InputValidationResult = await formHandler.validation
			.validate();

		if (validationResult.kind === InputValidationResultKind.Invalid) {
			setIsBusy(false);
			return;
		}


		let id: number | undefined = connectionId;
		try {
			if (id === undefined) {
				id = (await create(model, globalCancellation))
					.connectionId;
			} else {
				await update(id, model, globalCancellation);
			}

			onClose();
			onAfterSave({
				id,
				name: model.name,
				portalUrl: model.portalUrl
			});
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			if (!formHandler.validation.tryProcessServerError(e)) {
				alertManager.showError(localize("_ChangesNotSaved"));
			}
			setIsBusy(false);
		}
	};

	return (
		<>
			<Dialog hasBusyIndicator={isBusy} trackingId="liquit-connection-dialog">
				<Dialog.Header
					title={
						<FormattedText
							text={localize(titleKey)}
							thingsToReplace={[
								{ what: "{{Name}}", filler: localize("_Recast")}
							]}
						/>
					}
					onClose={onClose}
				/>
				<Dialog.Body>
					<Form onSubmit={submitHandler}>
						<FormItem label={localize("_Name")}>
							<TextInput {...formHandler.for("name").attachToTextInput()} />
							<ValidationError {...formHandler.for("name").attachToValidationError()} />
						</FormItem>

						<FormItem label={localize("_RecastPortalUrl")}>
							<TextInput
								{
									...formHandler.for("portalUrl")
										.attachToTextInput()
								}
								placeholder="https://company.liquit.com/"
							/>
							<ValidationError {...formHandler.for("portalUrl").attachToValidationError()} />
						</FormItem>

						<FormItem label={localize("_IdentitySource")}>
							<InputWithMoreInfo onMoreInfo={() => setActiveMoreInfoSetting("identitySource")} trackingId="identity-source-more-info">
								<TextInput {
									...formHandler.for("identitySource")
										.attachToTextInput()
								} />
							</InputWithMoreInfo>
							<ValidationError {...formHandler.for("identitySource").attachToValidationError()} />
						</FormItem>

						<FormItem label={localize("_ApplicationClientId")}>
							<InputWithMoreInfo onMoreInfo={() => setActiveMoreInfoSetting("applicationId")} trackingId="application-id-more-info">
								<TextInput {
									...formHandler.for("applicationId")
										.attachToTextInput()
								} />
							</InputWithMoreInfo>
							<ValidationError {...formHandler.for("applicationId").attachToValidationError()} />
						</FormItem>
					</Form>
				</Dialog.Body>
				<Dialog.Footer>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize(saveKey)}
							onClick={submitHandler}
							trackingId="save"
						/>

						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							onClick={onClose}
							trackingId="cancel"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>

			{activeFindableSetting && (
				<FindSettingDialog
					settingName={localize(activeFindableSetting.nameKey)}
					newLineSeparatedSteps={localize(activeFindableSetting.stepsKey)}
					trackingId={generateTrackingId("liquit-connection-more-info-dialog", `|${activeMoreInfoSetting}`)}
					onClose={() => setActiveMoreInfoSetting(undefined)}
				/>
			)}
		</>
	);
};
LiquitConnectionDialog.displayName = "LiquitConnectionDialog";

type FindableSettingEntry = {
	nameKey: LocalizationKey;
	stepsKey: LocalizationKey;
};

type FindableSetting = "applicationId" | "identitySource";

const _findSettingConfiguration: { [key in FindableSetting]: FindableSettingEntry; } = {
	applicationId: {
		nameKey: "_ApplicationClientId",
		stepsKey: "_WhereToFindApplicationClientIdExplanation"
	},
	identitySource: {
		nameKey: "_IdentitySource",
		stepsKey: "_WhereToFindIdentitySourceExplanation"
	}
};

export function openLiquitConnectionDialog(
	props: Omit<LiquitConnectionDialogProps, "onClose">
) {
	renderReactDialog((onClose) => <LiquitConnectionDialog {...props} onClose={onClose} />);
}
